import React, { useState, useEffect } from "react";
import {
    ArrowLeft,
    Loader2,
    Download,
    Users,
    Clock,
    RefreshCw,
    AlertCircle,
    FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useMeeting } from "../../../../context/MeetingContext";
import { MeetingSession, Transcription, TranscriptionSegment } from "../../../../types";

interface TranscriptionViewerProps {
    session: MeetingSession;
    onBack: () => void;
    onEditSpeakers: () => void;
}

// Color palette for speakers
const SPEAKER_COLORS = [
    { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300' },
    { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
    { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
    { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300' },
];

const TranscriptionViewer: React.FC<TranscriptionViewerProps> = ({
    session,
    onBack,
    onEditSpeakers
}) => {
    const {
        pollTranscriptionStatus,
        getTranscription,
        getDownloadUrl,
        refreshSession,
    } = useMeeting();

    const [transcription, setTranscription] = useState<Transcription | null>(null);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<'loading' | 'processing' | 'completed' | 'failed'>('loading');
    const [progress, setProgress] = useState<number>(0);

    // Get speaker name from mapping
    const getSpeakerName = (speakerLabel: string): string => {
        const mapping = session.speaker_mapping?.find(m => m.speaker_label === speakerLabel);
        if (mapping?.participant_name) {
            return mapping.participant_name;
        }
        // Default display
        const match = speakerLabel.match(/spk_(\d+)/);
        return match ? `Speaker ${parseInt(match[1]) + 1}` : speakerLabel;
    };

    // Get color for speaker
    const getSpeakerColor = (speakerLabel: string) => {
        const match = speakerLabel.match(/spk_(\d+)/);
        const index = match ? parseInt(match[1]) : 0;
        return SPEAKER_COLORS[index % SPEAKER_COLORS.length];
    };

    // Format timestamp
    const formatTimestamp = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Load transcription
    useEffect(() => {
        const loadTranscription = async () => {
            try {
                setLoading(true);

                // Check current status
                if (session.recording_status === 'completed') {
                    // Try to load transcription directly
                    const data = await getTranscription(session.id);
                    if (data) {
                        setTranscription(data);
                        setStatus('completed');
                    } else {
                        setStatus('failed');
                    }
                } else if (session.recording_status === 'transcribing' || session.recording_status === 'processing') {
                    // Poll for status
                    setStatus('processing');
                    pollForCompletion();
                } else if (session.recording_status === 'failed') {
                    setStatus('failed');
                }
            } catch (err) {
                console.error('Failed to load transcription:', err);
                setStatus('failed');
            } finally {
                setLoading(false);
            }
        };

        loadTranscription();
    }, [session.id, session.recording_status]);

    // Poll for transcription completion
    const pollForCompletion = async () => {
        try {
            const statusResponse = await pollTranscriptionStatus(session.id);

            if (statusResponse.progress_percent) {
                setProgress(statusResponse.progress_percent);
            }

            if (statusResponse.status === 'COMPLETED') {
                const data = await getTranscription(session.id);
                if (data) {
                    setTranscription(data);
                    setStatus('completed');
                    await refreshSession(session.id);
                }
            } else if (statusResponse.status === 'FAILED') {
                setStatus('failed');
            } else {
                // Continue polling
                setTimeout(pollForCompletion, 5000);
            }
        } catch (err) {
            console.error('Polling error:', err);
            setStatus('failed');
        }
    };

    // Handle download
    const handleDownload = async () => {
        try {
            const url = await getDownloadUrl(session.id);
            if (url) {
                window.open(url, '_blank');
            }
        } catch (err) {
            toast.error('Failed to get download URL');
        }
    };

    // Handle retry
    const handleRetry = () => {
        setLoading(true);
        setStatus('loading');
        pollForCompletion();
    };

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={onBack}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                        <h2 className="text-lg font-semibold">Transcription</h2>
                        <p className="text-sm text-gray-500 truncate max-w-[200px] sm:max-w-none">{session.title || 'Untitled Recording'}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <button
                        onClick={onEditSpeakers}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <Users className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit Speakers</span>
                        <span className="sm:hidden">Speakers</span>
                    </button>
                    {status === 'completed' && (
                        <button
                            onClick={handleDownload}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download Audio</span>
                            <span className="sm:hidden">Download</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-violet-500 animate-spin mb-4" />
                        <p className="text-gray-500">Loading transcription...</p>
                    </div>
                )}

                {!loading && status === 'processing' && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mb-4" />
                        <p className="text-gray-700 font-medium mb-2">Transcription in progress...</p>
                        {progress > 0 && (
                            <div className="w-48 bg-gray-200 rounded-full h-2 mb-2">
                                <div
                                    className="bg-amber-500 h-2 rounded-full transition-all"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        )}
                        <p className="text-sm text-gray-500">This may take a few minutes</p>
                    </div>
                )}

                {!loading && status === 'failed' && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <AlertCircle className="w-8 h-8 text-red-500 mb-4" />
                        <p className="text-gray-700 font-medium mb-2">Transcription failed</p>
                        <p className="text-sm text-gray-500 mb-4">There was an error processing the transcription</p>
                        <button
                            onClick={handleRetry}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Retry
                        </button>
                    </div>
                )}

                {!loading && status === 'completed' && transcription && (
                    <div className="space-y-4">
                        {/* Full text summary */}
                        {transcription.full_text && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Summary
                                </h3>
                                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                                    {transcription.full_text.slice(0, 500)}
                                    {transcription.full_text.length > 500 && '...'}
                                </p>
                            </div>
                        )}

                        {/* Transcription segments */}
                        <div className="space-y-3">
                            {transcription.content.map((segment, index) => {
                                const color = getSpeakerColor(segment.speaker_label);
                                const speakerName = getSpeakerName(segment.speaker_label);

                                return (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-lg border ${color.border} ${color.bg}`}
                                    >
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`font-medium ${color.text}`}>
                                                {speakerName}
                                            </span>
                                            <span className="text-xs text-gray-500 flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatTimestamp(segment.start_time)} - {formatTimestamp(segment.end_time)}
                                            </span>
                                        </div>
                                        <p className="text-gray-800">{segment.content}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!loading && status === 'completed' && (!transcription || transcription.content.length === 0) && (
                    <div className="flex flex-col items-center justify-center py-12">
                        <FileText className="w-8 h-8 text-gray-400 mb-4" />
                        <p className="text-gray-500">No transcription content available</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TranscriptionViewer;
